import React from 'react'
import PropTypes from 'prop-types'

import Icon from '../icon'
import TextInput from '../TextInput/TextInput'
import styles from './ClearableInput.module.scss'

/**
 * Label + control + help-text wrapper.
 *
 * Takes arbitrary children so existing controls (LocationSearch, DatePicker,
 * TelephoneInput, native selects) keep their own behaviour and are only
 * re-parented into the new visual treatment — not replaced.
 */
export function SettingsField({
  label,
  htmlFor,
  help,
  helpVariant,
  status,
  className = '',
  children,
}) {
  const helpClass = {
    error: styles.helpError,
    success: styles.helpSuccess,
  }[helpVariant]

  return (
    <div className={`${styles.field} ${className}`}>
      {label && (
        <label className={styles.label} htmlFor={htmlFor}>
          {label}
        </label>
      )}
      <div className={styles.inputRow}>
        <div className={styles.inputWrap}>{children}</div>
        {status === 'valid' && (
          <Icon
            name="circle-check"
            className={`${styles.status} ${styles.statusValid}`}
          />
        )}
        {status === 'invalid' && (
          <Icon
            name="circle-xmark"
            className={`${styles.status} ${styles.statusInvalid}`}
          />
        )}
      </div>
      {help && (
        <div className={`${styles.help} ${helpClass ?? ''}`}>{help}</div>
      )}
    </div>
  )
}

SettingsField.propTypes = {
  label: PropTypes.node,
  htmlFor: PropTypes.string,
  help: PropTypes.node,
  helpVariant: PropTypes.oneOf(['error', 'success']),
  status: PropTypes.oneOf(['empty', 'valid', 'invalid']),
  className: PropTypes.string,
  children: PropTypes.node,
}

/**
 * Text input with an ×-to-clear affordance, per the design.
 *
 * Thin wrapper over the shared TextInput's `clearable` mode, so there is one
 * clearable-input implementation in the app. NOTE the TextInput contract:
 * `onChange` receives the value string, not the event.
 */
export default function ClearableInput({
  id,
  name,
  type = 'text',
  value,
  onChange,
  onClear,
  onKeyDown,
  placeholder,
  clearLabel = 'Clear',
  inputRef,
}) {
  return (
    <TextInput
      className=""
      clearable={!!onClear}
      clearButtonAriaLabel={clearLabel}
      clearIcon="circle-xmark"
      id={id}
      inputRef={inputRef}
      // Reserve room so long values don't run under the clear button. Always
      // reserved in clearable mode: TextInput decides button visibility from
      // its own internal state, so a value-dependent padding could diverge.
      inputStyle={onClear ? { paddingRight: '2.25rem' } : undefined}
      name={name}
      onChange={onChange}
      onClear={onClear}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      type={type}
      value={value != null ? String(value) : ''}
    />
  )
}

ClearableInput.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  clearLabel: PropTypes.string,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
}
